import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, 
  eachDayOfInterval, isSameMonth, isSameDay, isToday, parseISO
} from 'date-fns';
import {
  Calendar as CalendarIcon, ChevronRight, Clock, 
  CheckSquare, Video, Users, Target, Bell
} from 'lucide-react';

// Event type configurations
const EVENT_TYPES = {
  task: { color: 'bg-blue-500', textColor: 'text-blue-600', icon: CheckSquare },
  meeting: { color: 'bg-purple-500', textColor: 'text-purple-600', icon: Video },
  activity: { color: 'bg-green-500', textColor: 'text-green-600', icon: Bell },
  followup: { color: 'bg-amber-500', textColor: 'text-amber-600', icon: Target }
};

// Mini Calendar Day
const MiniCalendarDay = memo(({ date, events, currentMonth, isSelected, onClick }) => {
  const hasEvents = events.some(e => {
    const eventDate = parseISO(e.date);
    return isSameDay(eventDate, date);
  });
  
  const eventTypes = events
    .filter(e => isSameDay(parseISO(e.date), date))
    .map(e => e.type)
    .filter((v, i, a) => a.indexOf(v) === i);
  
  const isCurrentMonth = isSameMonth(date, currentMonth);
  const today = isToday(date);
  
  return (
    <button
      className={`w-8 h-8 text-xs rounded-full flex flex-col items-center justify-center transition-colors
        ${!isCurrentMonth ? 'text-slate-300' : 'hover:bg-slate-100'}
        ${today ? 'bg-blue-100 text-blue-600 font-bold' : ''}
        ${isSelected ? 'ring-2 ring-blue-500' : ''}
      `}
      onClick={() => onClick(date)}
    >
      <span>{format(date, 'd')}</span>
      {hasEvents && (
        <div className="flex gap-0.5 mt-0.5">
          {eventTypes.slice(0, 3).map((type, idx) => (
            <div key={idx} className={`w-1 h-1 rounded-full ${EVENT_TYPES[type]?.color || 'bg-slate-400'}`} />
          ))}
        </div>
      )}
    </button>
  );
});

MiniCalendarDay.displayName = 'MiniCalendarDay';

// Upcoming Event Item
const UpcomingEventItem = memo(({ event }) => {
  const config = EVENT_TYPES[event.type] || EVENT_TYPES.activity;
  const Icon = config.icon;
  
  return (
    <div className="flex items-center gap-3 py-2 border-b last:border-0">
      <div className={`w-1.5 h-10 rounded-full ${config.color}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{event.title}</p>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {event.time || format(parseISO(event.date), 'MMM d')}
          {event.contact_name && (
            <>
              <span className="mx-1">•</span>
              <Users className="h-3 w-3" />
              {event.contact_name}
            </>
          )}
        </p>
      </div>
      <Icon className={`h-4 w-4 ${config.textColor}`} />
    </div>
  );
});

UpcomingEventItem.displayName = 'UpcomingEventItem';

export const CalendarWidget = memo(({ events = [], selectedDate, onDateSelect }) => {
  const currentMonth = selectedDate || new Date();
  
  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Get upcoming events (next 7 days)
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    return events
      .filter(e => {
        const eventDate = parseISO(e.date);
        return eventDate >= today && eventDate <= nextWeek;
      })
      .sort((a, b) => parseISO(a.date) - parseISO(b.date))
      .slice(0, 5);
  }, [events]);

  // Today's events
  const todayEvents = useMemo(() => {
    return events.filter(e => isSameDay(parseISO(e.date), new Date()));
  }, [events]);

  return (
    <Card className="h-full" data-testid="calendar-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            Calendar
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/calendar" className="flex items-center gap-1">
              View All
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mini Calendar */}
        <div className="border rounded-lg p-3">
          <div className="text-center font-medium mb-2 text-sm">
            {format(currentMonth, 'MMMM yyyy')}
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
              <div key={idx} className="text-center text-xs text-muted-foreground font-medium w-8">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map(day => (
              <MiniCalendarDay
                key={day.toISOString()}
                date={day}
                events={events}
                currentMonth={currentMonth}
                isSelected={selectedDate && isSameDay(day, selectedDate)}
                onClick={onDateSelect}
              />
            ))}
          </div>
        </div>

        {/* Today's Summary */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
          <div>
            <p className="text-sm font-medium">Today</p>
            <p className="text-xs text-muted-foreground">{format(new Date(), 'EEEE, MMM d')}</p>
          </div>
          <div className="flex gap-2">
            {todayEvents.length > 0 ? (
              <>
                <Badge variant="outline" className="bg-white">
                  {todayEvents.filter(e => e.type === 'task').length} tasks
                </Badge>
                <Badge variant="outline" className="bg-white">
                  {todayEvents.filter(e => e.type === 'meeting').length} meetings
                </Badge>
              </>
            ) : (
              <Badge variant="outline" className="bg-white">No events</Badge>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div>
          <p className="text-sm font-medium mb-2">Upcoming</p>
          <ScrollArea className="h-[200px]">
            {upcomingEvents.length > 0 ? (
              <div className="pr-2">
                {upcomingEvents.map((event, idx) => (
                  <UpcomingEventItem key={event.id || idx} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No upcoming events</p>
              </div>
            )}
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
});

CalendarWidget.displayName = 'CalendarWidget';

export default CalendarWidget;
